/**
 * 让站内跳转自动跟随当前语言。
 *
 * 路径化 i18n（/ 中文、/en 英文）之后，全站 58 处 <router-link to="/photoshop">
 * 和 17 处 $router.push('/shop-cart') 都是写死的中文路径。英文访客一点就被带回中文页，
 * 语言就断了 —— 登录成功跳 /profile、下单成功跳 /shop-cart 都踩过这个坑。
 *
 * 与其逐个改调用点（还容易漏掉动态绑定的 :to 和后加的 push），不如在这里统一加前缀：
 * 位于 /en 时，站内路径自动变成 /en/...，具名路由自动变成 en-xxx。
 *
 * 只处理站内相对路径。外链、锚点、已经带 /en 前缀的路径都原样放行。
 *
 * 语言切换器是唯一需要绕过这层的地方（它的目的就是跨语言跳转），
 * 用 router.pushRaw / router.replaceRaw。
 */
import Vue from 'vue'

const EN_PREFIX = '/en'

function isInternalPath(p) {
  return typeof p === 'string' && p.startsWith('/') && !p.startsWith('//')
}

function localizePath(path, locale) {
  if (locale !== 'en' || !isInternalPath(path)) return path
  if (path === EN_PREFIX || path.startsWith(EN_PREFIX + '/')) return path
  return path === '/' ? EN_PREFIX : EN_PREFIX + path
}

function localizeTo(to, locale) {
  if (locale !== 'en' || !to) return to
  if (typeof to === 'string') return localizePath(to, locale)
  if (typeof to === 'object') {
    // 具名路由：路由表里英文那套的 name 统一是 en- 前缀
    if (to.name && !String(to.name).startsWith('en-')) {
      return { ...to, name: `en-${to.name}` }
    }
    if (to.path) return { ...to, path: localizePath(to.path, locale) }
  }
  return to
}

function localeOf(path) {
  return /^\/en(\/|$)/.test(path || '') ? 'en' : 'zh'
}

/** 覆盖 <router-link>，让模板里的 to 跟随当前语言 */
function installLink() {
  const Original = Vue.component('RouterLink')
  if (!Original) return

  const Localized = {
    name: 'RouterLink',
    functional: true,
    props: { ...Original.options.props },
    render(h, ctx) {
      // 语言取自当前 URL，与 seo.js / main.js 的判定保持一致
      const locale = localeOf(ctx.parent.$route && ctx.parent.$route.path)
      const props = { ...ctx.props, to: localizeTo(ctx.props.to, locale) }
      return h(Original, { ...ctx.data, props }, ctx.children)
    }
  }

  Vue.component('RouterLink', Localized)
  Vue.component('router-link', Localized)
}

/**
 * 覆盖 router.push / router.replace。
 * 代码里的跳转和模板里的链接必须走同一套规则，否则「点链接是英文、下单跳转变中文」。
 */
function installProgrammatic(router) {
  if (!router || router.pushRaw) return

  router.pushRaw = router.push.bind(router)
  router.replaceRaw = router.replace.bind(router)

  ;['push', 'replace'].forEach(method => {
    const original = router[method].bind(router)
    router[method] = function (to, onComplete, onAbort) {
      const locale = localeOf(router.currentRoute && router.currentRoute.path)
      return original(localizeTo(to, locale), onComplete, onAbort)
    }
  })
}

export default function installLocalizedNavigation(router) {
  installLink()
  installProgrammatic(router)
}

export { localizePath, localizeTo, localeOf }
